import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, you would send the form data to your backend
    // For now, we'll just simulate a successful submission
    setFormStatus({
      submitted: true,
      success: true,
      message: 'Thank you for your message. We will get back to you soon!'
    });

    // Reset form after submission
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              Contact Us
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-500">
              We'd love to hear from you. Get in touch with our team.
            </p>
          </div>
        </div>
      </div>

      {/* Contact information section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:grid lg:grid-cols-5 lg:gap-8">
                <div className="lg:col-span-2">
                  <h2 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
                    Get in Touch
                  </h2>
                  <p className="mt-3 text-lg text-gray-500">
                    Have questions about our services? Need help with your account? Our team is here to assist you.
                  </p>
                  <dl className="mt-8 text-base text-gray-500">
                    <div className="mt-6">
                      <dt className="sr-only">Email</dt>
                      <dd className="flex">
                        <svg className="flex-shrink-0 h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="ml-3">support@explanatum.co.uk</span>
                      </dd>
                    </div>
                    
                    <div className="mt-3">
                      <dt className="sr-only">Office Address</dt>
                      <dd className="flex">
                        <svg className="flex-shrink-0 h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="ml-3">
                          3 Heathmoor Road<br />
                          Wirral, CH46 7UN
                        </span>
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-8">
                    <h3 className="text-lg font-medium text-gray-900">Business Hours</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Monday - Friday: 9:00 AM - 5:00 PM (GMT)<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
                <div className="mt-12 lg:mt-0 lg:col-span-3">
                  <div className="bg-white py-8 px-6 shadow rounded-lg sm:px-10">
                    {formStatus.submitted && (
                      <div className={`mb-6 p-4 rounded-md ${formStatus.success ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className={`text-sm ${formStatus.success ? 'text-green-700' : 'text-red-700'}`}>
                          {formStatus.message}
                        </p>
                      </div>
                    )}
                    <form className="space-y-6" onSubmit={handleSubmit}>
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <div className="mt-1">
                          <input
                            id="name"
                            name="name"
                            type="text"
                            autoComplete="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <div className="mt-1">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                          Subject
                        </label>
                        <div className="mt-1">
                          <select
                            id="subject"
                            name="subject"
                            required
                            value={formData.subject}
                            onChange={handleChange}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          >
                            <option value="">Please select</option>
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Technical Support">Technical Support</option>
                            <option value="Billing Question">Billing Question</option>
                            <option value="Feature Request">Feature Request</option>
                            <option value="Partnership Opportunity">Partnership Opportunity</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                          Message
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="message"
                            name="message"
                            rows={4}
                            required
                            value={formData.message}
                            onChange={handleChange}
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <button
                          type="submit"
                          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Send Message
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Can't find the answer you're looking for? Contact our support team.
            </p>
          </div>
          <div className="mt-12">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-12">
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">How do I get started with Explanatum?</dt>
                <dd className="mt-2 text-base text-gray-500">Simply sign up for an account and follow our guided setup process. You can start generating business ideas immediately after registration.</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">What payment methods do you accept?</dt>
                <dd className="mt-2 text-base text-gray-500">We accept all major credit cards, PayPal, and bank transfers through our secure payment processor, Paddle.com.</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">Can I cancel my subscription at any time?</dt>
                <dd className="mt-2 text-base text-gray-500">Yes, you can cancel your subscription at any time through your account settings. You'll continue to have access until the end of your current billing period.</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">How secure is my business data at Explanatum Limited?</dt>
                <dd className="mt-2 text-base text-gray-500">We take data security seriously. All your data is encrypted both in transit and at rest, and we follow industry best practices for data protection.</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
